/*!
 * Copyright 2020 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {Injectable} from "@angular/core";
import {SIDE_MENU_NOTIFICATION_CENTER} from "@app/models/constants/side-menu.constants";
import {PushManagerService} from "@app/modules/base/services/push-manager.service";
import {environment} from "@env/environment";
import {MenuController} from "@ionic/angular";
import {Action, Selector, State, StateContext, Store} from "@ngxs/store";
import {NextNotificatorService} from "@trec/api";
import {TrecApiError, TrecError} from "@trec/api/errors";
import {TrecTokenRef} from "@trec/api/token-ref";
import {from, of, throwError} from "rxjs";
import {catchError, delay, filter, first, switchMap, switchMapTo, tap} from "rxjs/operators";
import {CommonState} from "../common/common.state";
import {PushMsgActions} from "./push-msg.actions";

/**
 * Ngxs push msg state.
 */
export interface PushMsgStateModel {
  /**
   * Last error occurred.
   */
  error: TrecError | TrecApiError | null;
}

@State<PushMsgStateModel>({
  name: "pushMsg",
  defaults: {
    error: null,
  },
})
@Injectable()
export class PushMsgState {
  constructor(
    private store: Store,
    private nextNotService: NextNotificatorService,
    private pushManager: PushManagerService,
    private trecTokenRef: TrecTokenRef,
    private menuCtrl: MenuController
  ) { }

  // Selectors ////////////////////////////////////////////////////////////////////////////////////

  @Selector() static error(state: PushMsgStateModel) {
    return state.error;
  }

  // Actions //////////////////////////////////////////////////////////////////////////////////////

  @Action(PushMsgActions.SendFCMToken, {cancelUncompleted: true})
  sendFCMToken(
    ctx: StateContext<PushMsgStateModel>,
    {payload: {fcmToken}}: PushMsgActions.SendFCMToken
  ) {
    const trecUserId = this.store.selectSnapshot(CommonState.loggedUserData)?.personalData?.trecUserId;
    return of(null).pipe(
      delay(500), // Wait some time before running the action -> it could be cancelled by another incoming one
      // Wait for trec token
      switchMapTo(
        this.trecTokenRef.getAuthInfo().pipe(
          filter((t) => !!t),
          first()
        )
      ),
      switchMapTo(this.nextNotService.registration({nomeApp: environment.appId, tokenFirebase: fcmToken, deviceType: 'web', userId: trecUserId!})),
      tap(() => {
        // Ok -> Update sync time
        localStorage.setItem(this.pushManager.KEY_LAST_FCM_TOKEN_SYNC, new Date().toISOString());
        ctx.patchState({error: null});
      }),
      catchError((err) =>
        ctx.dispatch(new PushMsgActions.Error(err)).pipe(switchMap((e) => throwError(e)))
      )
    );
  }

  @Action(PushMsgActions.InvalidateFCMToken)
  invalidateFCMToken(ctx: StateContext<PushMsgStateModel>, { }: PushMsgActions.InvalidateFCMToken) {
    return from(this.pushManager.invalidateCurrentFcmToken()).pipe(
      catchError((err) =>
        ctx.dispatch(new PushMsgActions.Error(TrecError.from(err, "fcm_token_invalidation_err")))
      )
    );
  }

  @Action(PushMsgActions.Error)
  actionError(ctx: StateContext<PushMsgStateModel>, {payload}: PushMsgActions.Error) {
    ctx.patchState({error: payload});

    if (payload.code === "invalid_instance_id") {
      // 1) Webapp received a FCM token from the Firebase library
      // 2) Webapp sent the FCM token to the TreC backend
      // 3) The backend refused the token (probably, because previously, during a logout phase, the webapp invalidated the FCM token)
      // To exit from this condition (anc get a new valid FCM token), the user needs to reset the browser's notification permissions

      // Show to user "notification center" and guide to reset the permissions
      this.menuCtrl.open(SIDE_MENU_NOTIFICATION_CENTER);
    }
  }
}
